<template>
  <div
    v-if="loadingTrustedContact"
    class="tw-mt-[20%] tw-flex tw-justify-center tw-h-40"
    tabindex="0"
  >
    <WdsSpinner :size="40" indeterminate />
  </div>
  <div v-else class="tw-flex tw-flex-col tw-h-full" tabindex="0">
    <ManageForm
      :is-editing="true"
      :name-input-disabled="true"
      :email-input-disabled="true"
      :message-input-hidden="true"
      :initial-name="name"
      :initial-email="email"
      :initial-links="links"
      :initial-attachments="attachments"
      :initial-skills="skills.map((skill) => skill.name)"
      :initial-external-identifier="externalIdentifier"
      :initial-custom-field-values="customFieldValues"
      :on-cancel="onCancel"
      :on-confirm="onConfirm"
    />
  </div>
</template>

<script setup>
import gql from 'graphql-tag'
import { useQuery } from '@vue/apollo-composable'
import { defineEmits, defineProps, ref } from 'vue'
import { WdsSpinner } from '@worksome/wds-components'

import ManageForm from './ManageForm.vue'
import { publicApi } from '@/app-bootstrapping/SetUpApollo'
import { UpdateTrustedContactMutation } from '@/components/Contacts/CompanyFreelancer/mutations/UpdateTrustedContactMutation'

const props = defineProps({
  trustedContactId: {
    type: [String, null],
    required: true,
  },
})

const emit = defineEmits(['cancel', 'updated'])

const loadingTrustedContact = ref(true)
const name = ref({
  firstName: '',
  middleName: '',
  lastName: '',
})
const email = ref('')
const externalIdentifier = ref('')
const skills = ref([])
const links = ref([])
const attachments = ref([])
const customFieldValues = ref([])

const { onResult } = useQuery(
  gql`
    query trustedContact($id: ID!) {
      trustedContact(id: $id) {
        id
        worker {
          firstName
          middleName
          lastName
          email
        }
        skills {
          id
          name
        }
        links
        externalIdentifier
        attachments {
          id
          name
          url
        }
        customFieldValues {
          id
          content
          displayValue
          customField {
            id
            fieldType
          }
          customFieldOption {
            id
            value
          }
        }
      }
    }
  `,
  {
    id: props.trustedContactId,
  },
  {
    clientId: 'publicApi',
    fetchPolicy: 'no-cache',
  },
)

onResult((result) => {
  const trustedContact = result.data?.trustedContact

  if (trustedContact) {
    name.value = {
      firstName: trustedContact.worker?.firstName ?? '',
      middleName: trustedContact.worker?.middleName ?? '',
      lastName: trustedContact.worker?.lastName ?? '',
    }
    email.value = trustedContact.worker.email
    links.value = trustedContact.links.map((link) => ({
      title: link,
      url: link,
    }))
    attachments.value = trustedContact.attachments
    skills.value = trustedContact.skills
    externalIdentifier.value = trustedContact.externalIdentifier
    customFieldValues.value = trustedContact.customFieldValues

    loadingTrustedContact.value = false
  }
})

function onCancel() {
  emit('cancel')
}

function onConfirm(data) {
  return publicApi
    .mutate({
      mutation: UpdateTrustedContactMutation,
      variables: {
        input: {
          id: props.trustedContactId,
          links: data.links.value.map((link) => link.url),
          attachments: data.attachments.value.map((attachment) => attachment.id),
          skills: data.skills.value,
          externalIdentifier: data.externalIdentifier.value,
          customFieldValues: data.customFieldValues.value,
        },
      },
    })
    .then(() => {
      emit('updated')

      emit('cancel')
    })
    .catch((error) => {
      throw error
    })
}
</script>
